import { DispatchManifestStopDetailStatus } from "app/data/city-delivery/dispatch-board/models";
import type { SortDirection } from "./driverGroupSortFilter";
import { classifyPickupTiming } from "./usePickupAssignmentDriversColumn";
import type { UnassignedGroupByMode, UnassignedRoute, UnassignedStopGroup } from "../unassignedPickupTypes";
import { DEFAULT_UNASSIGNED_GROUP_BY } from "../unassignedPickupTypes";

export type UnassignedUrgencyFacet = "late" | "at_risk" | "on_time";

export type UnassignedGroupStatusFacet =
  | DispatchManifestStopDetailStatus.PENDING
  | DispatchManifestStopDetailStatus.IN_PROGRESS
  | DispatchManifestStopDetailStatus.COMPLETED
  | DispatchManifestStopDetailStatus.EXCEPTIONS;

export type UnassignedRouteFacets = {
  /** Empty = all urgencies. */
  urgency: UnassignedUrgencyFacet[];
  /** Empty = all group statuses. */
  groupStatus: UnassignedGroupStatusFacet[];
};

export function defaultUnassignedToolbarFacets(): UnassignedRouteFacets {
  return { urgency: [], groupStatus: [] };
}

export type UnassignedRouteSortKey = "route_name" | "pickup_count" | "stop_count";

export const UNASSIGNED_ROUTE_SORT_KEYS: readonly UnassignedRouteSortKey[] = [
  "route_name",
  "pickup_count",
  "stop_count",
] as const;

export function isUnassignedRouteSortKey(v: unknown): v is UnassignedRouteSortKey {
  return typeof v === "string" && (UNASSIGNED_ROUTE_SORT_KEYS as readonly string[]).includes(v);
}

export const DEFAULT_UNASSIGNED_SORT_KEY: UnassignedRouteSortKey = "route_name";

export const DEFAULT_UNASSIGNED_SORT_DIRECTION: SortDirection = "asc";

const URGENCY_SET = new Set<string>(["late", "at_risk", "on_time"]);
const STATUS_SET = new Set<string>(Object.values(DispatchManifestStopDetailStatus));

export function sanitizeUnassignedRouteFacets(raw: unknown): UnassignedRouteFacets {
  const d = defaultUnassignedToolbarFacets();
  if (!raw || typeof raw !== "object") return d;
  const o = raw as Record<string, unknown>;
  const urgency = (Array.isArray(o.urgency) ? o.urgency : []).filter(
    (x): x is UnassignedUrgencyFacet => typeof x === "string" && URGENCY_SET.has(x)
  );
  const groupStatus = (Array.isArray(o.groupStatus) ? o.groupStatus : []).filter(
    (x): x is UnassignedGroupStatusFacet =>
      typeof x === "string" && STATUS_SET.has(x)
  );
  return { urgency, groupStatus };
}

/** Worst pickup timing for a stop group (late beats at-risk). */
export function stopWorstUrgency(stop: UnassignedStopGroup): UnassignedUrgencyFacet {
  let atRisk = false;
  for (const p of stop.pickups) {
    const t = classifyPickupTiming(
      p.pickupDateForTiming,
      p.readyTimeForTiming,
      p.closeTimeForTiming,
      p.status[0] ?? ""
    );
    if (t === "late") return "late";
    if (t === "at_risk") atRisk = true;
  }
  return atRisk ? "at_risk" : "on_time";
}

export function stopMatchesUnassignedFacets(
  stop: UnassignedStopGroup,
  facets: UnassignedRouteFacets
): boolean {
  const uOk =
    facets.urgency.length === 0 || facets.urgency.includes(stopWorstUrgency(stop));
  const sOk =
    facets.groupStatus.length === 0 ||
    facets.groupStatus.includes(stop.groupStopStatus as UnassignedGroupStatusFacet);
  return uOk && sOk;
}

export function filterUnassignedRoutesByFacets(
  routes: UnassignedRoute[],
  facets: UnassignedRouteFacets
): UnassignedRoute[] {
  if (
    facets.urgency.length === 0 &&
    facets.groupStatus.length === 0
  ) {
    return routes;
  }
  return routes
    .map((route) => {
      const stops = route.stops.filter((s) => stopMatchesUnassignedFacets(s, facets));
      const count = stops.reduce((sum, st) => sum + st.pickups.length, 0);
      return { ...route, stops, count };
    })
    .filter((r) => r.stops.length > 0);
}

function compareRouteName(a: UnassignedRoute, b: UnassignedRoute): number {
  return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
}

export function sortUnassignedRoutes(
  routes: UnassignedRoute[],
  sortKey: UnassignedRouteSortKey,
  direction: SortDirection
): UnassignedRoute[] {
  const dir = direction === "asc" ? 1 : -1;
  const out = [...routes];
  out.sort((a, b) => {
    let cmp = 0;
    switch (sortKey) {
      case "route_name":
        cmp = compareRouteName(a, b) * dir;
        break;
      case "pickup_count":
        cmp = (a.count - b.count) * dir;
        break;
      case "stop_count":
        cmp = (a.stops.length - b.stops.length) * dir;
        break;
      default:
        cmp = 0;
    }
    if (cmp !== 0) return cmp;
    return compareRouteName(a, b);
  });
  return out;
}

function sortedStringTuple<T extends string>(arr: T[]): string {
  return [...arr].sort().join(",");
}

export function unassignedFacetsEqual(a: UnassignedRouteFacets, b: UnassignedRouteFacets): boolean {
  return (
    sortedStringTuple(a.urgency) === sortedStringTuple(b.urgency) &&
    sortedStringTuple(a.groupStatus) === sortedStringTuple(b.groupStatus)
  );
}

export function unassignedToolbarMatchesDefaults(
  groupBy: UnassignedGroupByMode,
  sortKey: UnassignedRouteSortKey,
  sortDirection: SortDirection
): boolean {
  return (
    groupBy === DEFAULT_UNASSIGNED_GROUP_BY &&
    sortKey === DEFAULT_UNASSIGNED_SORT_KEY &&
    sortDirection === DEFAULT_UNASSIGNED_SORT_DIRECTION
  );
}

const STORAGE_PREFIX = "pickupAssignmentV2:unassignedToolbar";

export type UnassignedToolbarPersisted = {
  sortKey: UnassignedRouteSortKey;
  sortDirection: SortDirection;
  groupBy: UnassignedGroupByMode;
};

export function loadUnassignedToolbarState(
  terminalId: number | null
): Partial<UnassignedToolbarPersisted> | null {
  if (terminalId == null || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}:${terminalId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<UnassignedToolbarPersisted>;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

export function saveUnassignedToolbarState(
  terminalId: number | null,
  state: UnassignedToolbarPersisted
): void {
  if (terminalId == null || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}:${terminalId}`, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}
